/**
 * 滚动边缘渐隐遮罩 · 给虚拟滚动容器上 / 下边缘加透明渐变，让内容滚入滚出更平滑。
 *
 * 用 mask-image 线性渐变实现：遮罩相对容器视口固定（不随内容滚动），内容从渐变区
 * 下方滚过，靠近上 / 下边缘时自然淡出 / 淡入，替代原本的硬裁切边缘。
 *
 * 滚动位置感知：滚到最顶时不加上渐变、滚到最底时不加下渐变，避免首 / 末行在静止
 * 时被无谓遮淡；短列表（不溢出、无法滚动）则两端都不加。
 *
 * 用法：把 onScroll 透给 react-virtualized 的 <Grid/List onScroll>，
 *       把 maskStyle 并进它们的 style。
 */
import { useCallback, useState } from 'react'
import type { CSSProperties } from 'react'

/** 上 / 下渐变区高度（px） · 调大更柔、调小更利落 */
const FADE = 24

interface ScrollInfo {
  scrollTop: number
  scrollHeight: number
  clientHeight: number
}

interface Edges {
  top: boolean
  bottom: boolean
}

/** 据上 / 下是否还有内容，拼出 mask 渐变；两端都无内容则返回 undefined（不加 mask） */
function buildMask(top: boolean, bottom: boolean): string | undefined {
  if (!top && !bottom) return undefined
  const head = top ? `transparent 0, #000 ${FADE}px` : '#000 0'
  const tail = bottom
    ? `#000 calc(100% - ${FADE}px), transparent 100%`
    : '#000 100%'
  return `linear-gradient(to bottom, ${head}, ${tail})`
}

export function useEdgeFadeMask(): {
  onScroll: (info: ScrollInfo) => void
  maskStyle: CSSProperties
} {
  const [edges, setEdges] = useState<Edges>({ top: false, bottom: false })

  const onScroll = useCallback((info: ScrollInfo): void => {
    const top = info.scrollTop > 1
    const bottom = info.scrollTop + info.clientHeight < info.scrollHeight - 1
    // 仅在上 / 下边缘状态切换时更新，避免每帧滚动都重渲染
    setEdges((prev) =>
      prev.top === top && prev.bottom === bottom ? prev : { top, bottom }
    )
  }, [])

  const maskImage = buildMask(edges.top, edges.bottom)
  const maskStyle: CSSProperties = maskImage
    ? { WebkitMaskImage: maskImage, maskImage }
    : {}

  return { onScroll, maskStyle }
}
